import { memo } from 'react';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import { Toll as CreditIcon } from '@mui/icons-material';
import type { NumberMode } from '../types';

export interface TierCreditBadgeProps {
  /** Number of credits, or calculate from numberMode */
  credits?: number;
  /** Number mode to derive credit cost from (if credits not provided) */
  numberMode?: NumberMode;
  /** Size of the badge */
  size?: ChipProps['size'];
  /** Variant style */
  variant?: ChipProps['variant'];
  /** Show icon */
  showIcon?: boolean;
  /** Custom tooltip text */
  tooltip?: string;
}

/**
 * Get credit cost from number mode (fallback values)
 */
function getCreditCostFromMode(mode: NumberMode | undefined): number {
  switch (mode) {
    case 'half':
      return 2;
    case 'quarter':
      return 4;
    case 'single':
    default:
      return 1;
  }
}

/**
 * Get color based on credit cost
 * - 1 credit: default (gray)
 * - 2 credits: primary (blue)
 * - 4 credits: secondary (purple)
 */
function getColorForCredits(credits: number): ChipProps['color'] {
  if (credits >= 4) return 'secondary';
  if (credits >= 2) return 'primary';
  return 'default';
}

/**
 * Reusable badge showing credit cost for board tiers.
 * Displays different colors based on credit amount:
 * - 1 credit: gray (default tier)
 * - 2 credits: blue (half sets)
 * - 4 credits: purple (quarter sets)
 */
export const TierCreditBadge = memo<TierCreditBadgeProps>(({
  credits,
  numberMode,
  size = 'small',
  variant = 'outlined',
  showIcon = true,
  tooltip,
}) => {
  const creditCount = credits ?? getCreditCostFromMode(numberMode);
  const color = getColorForCredits(creditCount);
  const label = `${creditCount} Credit${creditCount !== 1 ? 's' : ''}`;
  const tooltipText = tooltip ?? `This board costs ${creditCount} credit${creditCount !== 1 ? 's' : ''} to create`;

  const chip = (
    <Chip
      size={size}
      variant={variant}
      label={label}
      color={color}
      icon={showIcon ? <CreditIcon sx={{ fontSize: size === 'small' ? '0.875rem' : '1rem' }} /> : undefined}
      sx={{
        '& .MuiChip-icon': {
          marginLeft: '4px',
        },
      }}
    />
  );

  if (tooltip === '') {
    return chip;
  }

  return (
    <Tooltip title={tooltipText}>
      {chip}
    </Tooltip>
  );
});

TierCreditBadge.displayName = 'TierCreditBadge';

export default TierCreditBadge;
